import { trackError } from "@/utils/error-monitoring";

export async function loadScript(src: string, id: string) {
  if (document.getElementById(id)) {
    return;
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.id = id;
    script.onload = resolve;
    script.onerror = () => {
      // Drop the tag so the `getElementById` guard above doesn't treat a failed
      // load as an already-loaded script on the next attempt.
      script.remove();
      // Reject with a real Error: the DOM error event carries no message, so
      // Sentry would group every failure under the calling function's name.
      reject(new Error(`Failed to load script: ${src}`));
    };
    document.body.appendChild(script);
  });
}

// Waits for the render that mounts or opens the element. `preventScroll`
// leaves the scroll to the caller.
export function focusAfterDOMUpdate(
  getElement: () => HTMLElement | null | undefined,
) {
  ensureAfterDOMUpdate(() => {
    getElement()?.focus({ preventScroll: true });
  });
}

function ensureAfterDOMUpdate(callback: () => void) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      callback();
    });
  });
}

const TIME_TO_FINISH_SCROLLING = 1000;

const pendingScrollsToTop = new Set<() => void>();

// Returns a function that stops the scroll if it has not started yet, and
// stops its fallback. A smooth scroll that has started cannot be stopped; only
// a new scroll replaces it.
export function scrollToTop(delay = 0) {
  let cancelled = false;
  let fallback: ReturnType<typeof setTimeout> | undefined;

  const cancel = () => {
    cancelled = true;
    clearTimeout(start);
    clearTimeout(fallback);
    pendingScrollsToTop.delete(cancel);
  };

  const start = setTimeout(() => {
    ensureAfterDOMUpdate(() => {
      if (cancelled) {
        return;
      }

      try {
        window.scrollTo({ top: 0, behavior: "smooth" });

        fallback = setTimeout(() => {
          pendingScrollsToTop.delete(cancel);
          // Additional fallback for iOS Safari issues - only if smooth scroll failed
          if (window.scrollY > 0) {
            window.scrollTo(0, 0);
          }
        }, TIME_TO_FINISH_SCROLLING);
      } catch (_error) {
        pendingScrollsToTop.delete(cancel);
        // Fallback for older browsers
        window.scrollTo(0, 0);
      }
    });
  }, delay);

  pendingScrollsToTop.add(cancel);

  return cancel;
}

function cancelPendingScrollsToTop() {
  pendingScrollsToTop.forEach((cancel) => cancel());
}

// `focus()` alone does not scroll reliably: browsers skip the native focus
// scroll when the element is only partially hidden. `preventScroll` keeps the
// native jump from fighting the smooth scroll; Safari before 15.4 ignores
// `behavior` and jumps instantly, which is fine.
// A smooth scroll fixes its target when it starts, so both wait for the error
// messages that the caller set in the same tick to render and grow the page.
// Focus also waits, so a screen reader reads the linked error on arrival.
export function focusAndScrollIntoView(
  element: HTMLElement | null | undefined,
) {
  if (!element) {
    return;
  }

  // A scroll to the top that has not started yet, such as the one for a step
  // that just opened, or its fallback for iOS, would carry the page away from
  // the field again (#1179). A smooth scroll to the top that already runs
  // cannot be cancelled: the `scrollIntoView` below replaces it.
  cancelPendingScrollsToTop();

  ensureAfterDOMUpdate(() => {
    element.focus({ preventScroll: true });
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    scrollAgainAfterAnimations(element);
  });
}

// A step that is still opening or closing moves the field after the smooth
// scroll fixed its target, so the field can end at the edge of the viewport
// or out of it (#1179). An animation that never ends, such as a spinner, is
// not waited for. The form shares the document with the host page, so the
// wait stops after this time and the second scroll runs then, even if a host
// animation still runs.
const MAX_WAIT_FOR_ANIMATIONS = 600;

function scrollAgainAfterAnimations(element: HTMLElement) {
  if (typeof document.getAnimations !== "function") {
    return;
  }

  const running = document
    .getAnimations()
    .filter(
      (animation) => animation.effect?.getComputedTiming().endTime !== Infinity,
    );

  if (running.length === 0) {
    return;
  }

  const timeout = new Promise<void>((resolve) => {
    setTimeout(resolve, MAX_WAIT_FOR_ANIMATIONS);
  });

  // A cancelled animation rejects `finished`. Any animation in the document
  // can be cancelled, also on the host page, so a rejection must not stop the
  // scroll for the animations that did end.
  Promise.race([
    Promise.allSettled(running.map((animation) => animation.finished)),
    timeout,
  ])
    .then(() => {
      // In the wait, the donor can fix the field and go to the next step, or
      // move to another field. A scroll back to this field would then pull the
      // page away from where the donor is.
      if (element.isConnected && document.activeElement === element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    })
    .catch((error) => trackError(error as Error));
}

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = "";
};

export function enableBeforeUnload() {
  window.addEventListener("beforeunload", handleBeforeUnload);
}

export function disableBeforeUnload() {
  window.removeEventListener("beforeunload", handleBeforeUnload);
}
